import { useCallback, useEffect, useRef, useState } from 'react';
import { cloneDeep, isEqual } from 'lodash';
import { getValueByPath } from 'utils/getValueByPath';
import type {
  BulkClaimForm,
  BulkEditClaimForm,
  CreateClaimForm,
  EditClaimForm,
  Tariff,
} from 'interfaces';
import type { ClaimTariff } from '@billing/validation/tariff';
import {
  applyTimeToDateTime,
  calculateAnesthesiaUnitsFromTimes,
  determineAssistPercentage,
  generateNewRowItem,
  getDefaultTariffServiceEnd,
  getTariffServiceTimes,
  mapTariffsToRows,
  RowItem,
} from './utils/tariffHelpers';
import { DEFAULT_CLAIM_TARIFF_STATUS } from '@billing/validation/tariff';
import { useCurrentSiteStore } from 'store';
import { nowISO } from 'utils/dateUtils';
import { isAnesthesiaSpecialty } from '@billing/validation/specialty';
import type { Province } from '@billing/validation/payor';

interface UseTariffRowStateArgs {
  initialTariffs?: RowItem[];
  claimServiceStart?: string;
  claimServiceEnd?: string;
  specialtyCode?: string;
  province?: Province | null;
  specialtyLookup?: Array<{ code: string; description: string }>;
  hospitalCode?: string;
  serviceLocationIndicator?: string | null;
  defaultICD9?: string | null;
  onDirty: () => void;
}

type RowItemsUpdater = (previous: RowItem[]) => RowItem[];

export const useTariffRowState = ({
  initialTariffs = [],
  claimServiceStart,
  claimServiceEnd,
  specialtyCode,
  province,
  specialtyLookup = [],
  hospitalCode,
  serviceLocationIndicator,
  defaultICD9,
  onDirty,
}: UseTariffRowStateArgs) => {
  const { timezone } = useCurrentSiteStore();

  const isAnesthesiaAutoApplicable = useCallback(
    (tariff: Pick<Tariff, 'anesthesiaServiceValue'>) => {
      const anesthesiaServiceValue = Number(tariff.anesthesiaServiceValue);
      return (
        isAnesthesiaSpecialty(province, specialtyCode) &&
        Number.isFinite(anesthesiaServiceValue) &&
        anesthesiaServiceValue !== 0
      );
    },
    [province, specialtyCode]
  );

  const getAutoAnesthesiaUnits = useCallback(
    (serviceStart?: string | null, serviceEnd?: string | null) =>
      calculateAnesthesiaUnitsFromTimes(serviceStart, serviceEnd),
    []
  );

  const resolveHydratedAnesthesiaUnits = useCallback(
    (item: RowItem, persistedUnits?: number | null): RowItem => {
      if (!isAnesthesiaAutoApplicable(item)) {
        return {
          ...item,
          anesthesiaUnits: Number(item.anesthesiaUnits ?? 0),
          isAnesthesiaUnitsManual: false,
        };
      }

      const calculatedUnits = getAutoAnesthesiaUnits(
        item.serviceStart,
        item.serviceEnd
      );
      const normalizedPersistedUnits = Number(persistedUnits);
      const hasPersistedUnits =
        persistedUnits != null &&
        Number.isFinite(normalizedPersistedUnits) &&
        (normalizedPersistedUnits > 0 || item.isAnesthesiaUnitsManual);
      const isPersistedOverride =
        hasPersistedUnits && normalizedPersistedUnits !== calculatedUnits;
      const useOverride = item.isAnesthesiaUnitsManual || isPersistedOverride;
      const overrideUnits = hasPersistedUnits
        ? normalizedPersistedUnits
        : Number(item.anesthesiaUnits ?? 0);

      return {
        ...item,
        anesthesiaUnits: useOverride ? overrideUnits : calculatedUnits,
        isAnesthesiaUnitsManual: useOverride,
      };
    },
    [getAutoAnesthesiaUnits, isAnesthesiaAutoApplicable]
  );

  const initialRowsRef = useRef<RowItem[] | null>(null);
  if (!initialRowsRef.current) {
    initialRowsRef.current = mapTariffsToRows(
      initialTariffs,
      claimServiceStart,
      claimServiceEnd
    ).map((item, index) =>
      resolveHydratedAnesthesiaUnits(
        item,
        initialTariffs[index]?.anesthesiaUnits
      )
    );
  }

  const [items, setItemsState] = useState<RowItem[]>(initialRowsRef.current);
  const itemsRef = useRef(items);
  const setItems = useCallback((update: RowItemsUpdater): boolean => {
    const next = update(itemsRef.current);
    if (isEqual(itemsRef.current, next)) return false;
    itemsRef.current = next;
    setItemsState(next);
    return true;
  }, []);
  const commitItems = useCallback(
    (update: RowItemsUpdater): void => {
      if (setItems(update)) onDirty();
    },
    [onDirty, setItems]
  );

  const syncServiceStartFromClaim = useCallback(
    (serviceStart?: string) => {
      if (!serviceStart) return;

      commitItems((prev) =>
        prev.map((item) => {
          if (item.serviceStartOverridden) return item;
          if (item.serviceStart === serviceStart) return item;

          const nextItem = {
            ...item,
            serviceStart,
            anesthesiaUnits:
              isAnesthesiaAutoApplicable(item) && !item.isAnesthesiaUnitsManual
                ? getAutoAnesthesiaUnits(serviceStart, item.serviceEnd)
                : item.anesthesiaUnits,
          } as RowItem;

          return nextItem;
        })
      );
    },
    [getAutoAnesthesiaUnits, isAnesthesiaAutoApplicable, commitItems]
  );

  const syncServiceEndFromClaim = useCallback(
    (serviceEnd?: string) => {
      if (!serviceEnd) return;

      commitItems((prev) =>
        prev.map((item) => {
          if (item.serviceEndOverridden) return item;
          if (item.serviceEnd === serviceEnd) return item;

          const nextItem = {
            ...item,
            serviceEnd,
            anesthesiaUnits:
              isAnesthesiaAutoApplicable(item) && !item.isAnesthesiaUnitsManual
                ? getAutoAnesthesiaUnits(item.serviceStart, serviceEnd)
                : item.anesthesiaUnits,
          } as RowItem;

          return nextItem;
        })
      );
    },
    [getAutoAnesthesiaUnits, isAnesthesiaAutoApplicable, commitItems]
  );

  const hydrateRows = useCallback(
    (tariffs: RowItem[] = []) => {
      const nextItems = mapTariffsToRows(
        tariffs,
        claimServiceStart,
        claimServiceEnd,
        itemsRef.current
      ).map((item, index) =>
        resolveHydratedAnesthesiaUnits(item, tariffs[index]?.anesthesiaUnits)
      );

      setItems(() => nextItems);
    },
    [
      claimServiceEnd,
      claimServiceStart,
      resolveHydratedAnesthesiaUnits,
      setItems,
    ]
  );

  const handleStartTimeChange = useCallback(
    (rowID: string, timeValue: string) => {
      commitItems((prev) =>
        prev.map((item) => {
          if (item._rowID !== rowID) return item;

          const nextServiceStart = applyTimeToDateTime(
            item.serviceStart || nowISO(timezone),
            timeValue,
            timezone
          );
          if (!nextServiceStart || nextServiceStart === item.serviceStart) {
            return item;
          }

          const shouldSyncServiceEnd = !item.serviceEndOverridden;
          const nextServiceEnd = shouldSyncServiceEnd
            ? getDefaultTariffServiceEnd(nextServiceStart)
            : item.serviceEnd;

          return {
            ...item,
            serviceStart: nextServiceStart,
            serviceEnd: nextServiceEnd,
            serviceStartOverridden: true,
            anesthesiaUnits:
              isAnesthesiaAutoApplicable(item) && !item.isAnesthesiaUnitsManual
                ? getAutoAnesthesiaUnits(nextServiceStart, nextServiceEnd)
                : item.anesthesiaUnits,
            isDirty: true,
          };
        })
      );
    },
    [getAutoAnesthesiaUnits, isAnesthesiaAutoApplicable, commitItems]
  );

  const handleEndTimeChange = useCallback(
    (rowID: string, timeValue: string) => {
      commitItems((prev) =>
        prev.map((item) => {
          if (item._rowID !== rowID) return item;

          const nextServiceEnd = applyTimeToDateTime(
            item.serviceEnd ||
              getDefaultTariffServiceEnd(item.serviceStart || nowISO(timezone)),
            timeValue,
            timezone
          );
          if (!nextServiceEnd || nextServiceEnd === item.serviceEnd) {
            return item;
          }

          return {
            ...item,
            serviceEnd: nextServiceEnd,
            serviceEndOverridden: true,
            anesthesiaUnits:
              isAnesthesiaAutoApplicable(item) && !item.isAnesthesiaUnitsManual
                ? getAutoAnesthesiaUnits(item.serviceStart, nextServiceEnd)
                : item.anesthesiaUnits,
            isDirty: true,
          };
        })
      );
    },
    [getAutoAnesthesiaUnits, isAnesthesiaAutoApplicable, commitItems]
  );

  const addBlankRow = useCallback((): string => {
    const currentItems = itemsRef.current;
    const lastValid =
      [...currentItems].reverse().find((item) => item.icd9) ||
      currentItems[currentItems.length - 1] ||
      null;
    const newItem = generateNewRowItem({
      last: lastValid,
      claimServiceStart,
      claimServiceEnd,
      tariffs: currentItems,
    });
    commitItems((prev) => [
      ...prev,
      { ...newItem, icd9: defaultICD9 || newItem.icd9 },
    ]);
    return newItem._rowID;
  }, [claimServiceEnd, claimServiceStart, defaultICD9, commitItems]);

  const handleDeleteTariff = useCallback(
    (rowID: string) => {
      commitItems((prev) => {
        const next = prev.filter((item) => item._rowID !== rowID);
        return next;
      });
    },
    [commitItems]
  );

  const handleUnitsChange = useCallback(
    (rowID: string, newUnits: number) => {
      const parsedUnits = Number(newUnits);
      const safeUnits =
        !Number.isFinite(parsedUnits) || parsedUnits < 1 ? 1 : parsedUnits;

      commitItems((prev) => {
        const next = prev.map((item) => {
          if (item._rowID !== rowID) return item;

          const nextTotal =
            item.isManualTotal && item.total != null ? item.total : undefined;
          if (item.units === safeUnits && item.total === nextTotal) {
            return item;
          }

          return {
            ...item,
            units: safeUnits,
            total: nextTotal,
          };
        });

        return next;
      });
    },
    [commitItems]
  );

  const handlePercentageChange = useCallback(
    (rowID: string, newPct: number | undefined) => {
      const normalizedPct = newPct == null ? undefined : Number(newPct);
      const safePct =
        normalizedPct == null || Number.isNaN(normalizedPct)
          ? undefined
          : normalizedPct;

      commitItems((prev) => {
        const next = prev.map((item) => {
          if (item._rowID !== rowID) return item;

          const nextTotal =
            item.isManualTotal && item.total != null ? item.total : undefined;
          if (item.percentage === safePct && item.total === nextTotal) {
            return item;
          }

          return {
            ...item,
            percentage: safePct,
            total: nextTotal,
          };
        });

        return next;
      });
    },
    [commitItems]
  );

  const handleSplitChange = useCallback(
    (rowID: string, split: string) => {
      commitItems((prev) => {
        const next = prev.map((item) => {
          if (item._rowID !== rowID || item.split === split) return item;
          return { ...item, split };
        });
        return next;
      });
    },
    [commitItems]
  );

  const handleAnesthesiaUnitsChange = useCallback(
    (rowID: string, newUnits: number) => {
      const parsed = Number(newUnits);
      const safeUnits =
        !Number.isFinite(parsed) || parsed < 0
          ? 0
          : Math.min(96, Math.floor(parsed));
      commitItems((prev) => {
        const next = prev.map((item) => {
          if (item._rowID !== rowID) return item;
          if (
            item.anesthesiaUnits === safeUnits &&
            item.isAnesthesiaUnitsManual
          )
            return item;
          return {
            ...item,
            anesthesiaUnits: safeUnits,
            isAnesthesiaUnitsManual: true,
          };
        });
        return next;
      });
    },
    [commitItems]
  );

  const handleIncisionChange = useCallback(
    (rowID: string, incision: string | null) => {
      commitItems((prev) => {
        const next = prev.map((item) => {
          if (item._rowID !== rowID || item.incision === incision) return item;
          return { ...item, incision };
        });
        return next;
      });
    },
    [commitItems]
  );

  const handleICD9Change = useCallback(
    (rowID: string, icd9: string) => {
      commitItems((previous) => {
        if (!previous.some((item) => item._rowID === rowID)) return previous;

        return previous.map((item) => {
          const shouldUpdate =
            item._rowID === rowID || (icd9 !== '' && !item.icd9);
          if (!shouldUpdate || item.icd9 === icd9) return item;
          return { ...item, icd9 };
        });
      });
    },
    [commitItems]
  );

  const handleTariffSelection = useCallback(
    (newTariff: Tariff, targetRowID: string) => {
      const percentage =
        newTariff.prefix === 0
          ? determineAssistPercentage(specialtyCode || '', specialtyLookup)
          : newTariff.percentage ?? 100;

      commitItems((prev) => {
        const idx = prev.findIndex((row) => row._rowID === targetRowID);
        if (idx === -1) return prev;

        const rowToUpdate = prev[idx];
        const updatedRow = {
          _rowID: rowToUpdate._rowID,
          ...newTariff,
          originalRate: newTariff.rate,
          units: newTariff.units ?? 1,
          icd9: rowToUpdate.icd9 || newTariff.icd9 || '',
          split: newTariff.split || ' ',
          incision: rowToUpdate.incision ?? newTariff.incision ?? null,
          anesthesiaUnits: isAnesthesiaAutoApplicable(newTariff)
            ? getAutoAnesthesiaUnits(
                rowToUpdate.serviceStart,
                rowToUpdate.serviceEnd
              )
            : Number(
                rowToUpdate.anesthesiaUnits ?? newTariff.anesthesiaUnits ?? 0
              ),
          isAnesthesiaUnitsManual: false,
          percentage,
          // Leave `total` unset so the downstream tariff math can recompute the
          // correct amount with location, hospital, and override rules.
          total: undefined,
          isManualTotal: false,
          isDirty: false,
          status:
            rowToUpdate.status ||
            newTariff.status ||
            DEFAULT_CLAIM_TARIFF_STATUS,
          serviceStartOverridden: rowToUpdate.serviceStartOverridden,
          serviceEndOverridden: rowToUpdate.serviceEndOverridden,
          ...getTariffServiceTimes(
            rowToUpdate,
            claimServiceStart,
            claimServiceEnd
          ),
        } as RowItem;

        const next = [...prev];
        next[idx] = updatedRow;
        return next;
      });
    },
    [
      claimServiceStart,
      claimServiceEnd,
      getAutoAnesthesiaUnits,
      hospitalCode,
      isAnesthesiaAutoApplicable,
      commitItems,
      serviceLocationIndicator,
      specialtyCode,
      specialtyLookup,
    ]
  );

  const handleManualTotalSave = useCallback(
    (rowID: string, value: number | undefined) => {
      if (value == null || Number.isNaN(value)) {
        return false;
      }

      commitItems((prev) =>
        prev.map((item) =>
          item._rowID === rowID
            ? {
                ...item,
                total: Number(Number(value).toFixed(2)),
                isManualTotal: true,
              }
            : item
        )
      );
      return true;
    },
    [commitItems]
  );

  const handleManualTotalReset = useCallback(
    (rowID: string) => {
      commitItems((prev) => {
        const next = prev.map((item) => {
          if (item._rowID !== rowID) return item;
          if (!item.isManualTotal && item.total == null) {
            return item;
          }

          return {
            ...item,
            isManualTotal: false,
            total: undefined,
          };
        });
        return next;
      });
    },
    [commitItems]
  );

  return {
    items,
    setItems,
    hydrateRows,
    syncServiceStartFromClaim,
    syncServiceEndFromClaim,
    handleDeleteTariff,
    handleUnitsChange,
    handlePercentageChange,
    handleSplitChange,
    handleAnesthesiaUnitsChange,
    handleIncisionChange,
    handleICD9Change,
    handleStartTimeChange,
    handleEndTimeChange,
    handleTariffSelection,
    handleManualTotalSave,
    handleManualTotalReset,
    addBlankRow,
  };
};

type TariffFormValues =
  | CreateClaimForm
  | EditClaimForm
  | BulkClaimForm
  | BulkEditClaimForm;

type WatchedTariffs = Tariff[] | ClaimTariff[] | null | undefined;

type TariffForm = {
  getValues: () => TariffFormValues;
  setFieldValue: (path: string, tariffs: Tariff[]) => void;
  watch: (
    path: string,
    subscriber: (input: { value: WatchedTariffs }) => void
  ) => void;
};

export const useTariffFormSync = ({
  form,
  tariffPath,
  claimID,
  formTariffs,
  items,
  normalizedTariffs,
  hydrateRows,
  normalize,
}: {
  form: TariffForm;
  tariffPath: string;
  claimID?: string;
  formTariffs: Tariff[];
  items: RowItem[];
  normalizedTariffs: Tariff[];
  hydrateRows: (rows: RowItem[]) => void;
  normalize: (tariffs: Tariff[]) => RowItem[];
}): ((tariffs: Tariff[]) => void) => {
  const lastFormRef = useRef(cloneDeep(formTariffs));
  const sourceRef = useRef({ formTariffs, hydrateRows });
  sourceRef.current = { formTariffs, hydrateRows };
  const changedExternally = !isEqual(lastFormRef.current, formTariffs);
  if (changedExternally) lastFormRef.current = cloneDeep(formTariffs);

  useEffect(() => {
    if (changedExternally && !isEqual(items, formTariffs))
      hydrateRows(formTariffs as RowItem[]);
  }, [changedExternally, formTariffs, hydrateRows, items]);

  form.watch(tariffPath, ({ value }) => {
    const next = Array.isArray(value) ? (value as Tariff[]) : [];
    if (isEqual(lastFormRef.current, next)) return;
    lastFormRef.current = cloneDeep(next);
    if (!isEqual(items, next)) hydrateRows(next as RowItem[]);
  });

  useEffect(() => {
    sourceRef.current.hydrateRows(sourceRef.current.formTariffs as RowItem[]);
  }, [claimID]);

  const commitTariffs = useCallback(
    (tariffs: Tariff[]): void => {
      lastFormRef.current = cloneDeep(tariffs);
      form.setFieldValue(tariffPath, tariffs);
    },
    [form, tariffPath]
  );

  useEffect(() => {
    if (changedExternally) return;
    const current = normalize(
      (getValueByPath(form.getValues(), tariffPath) as Tariff[]) ?? []
    );
    if (!isEqual(current, normalizedTariffs)) commitTariffs(normalizedTariffs);
  }, [
    changedExternally,
    commitTariffs,
    form,
    normalize,
    normalizedTariffs,
    tariffPath,
  ]);
  return commitTariffs;
};
